import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, DollarSign, Calendar, Cog, Copy, Plus, Edit, Trash, X, Hash, Key, Clock, Palette, Sliders, Zap, Shield, CircleUserRound, Antenna, Fuel } from 'lucide-react';
import api from '../../utils/api';

interface Vehicle {
  id: number;
  brand_id: number;
  plate_number: string;
  model: string;
  brand_name: string;
  logo_url: string;
  fuel_type: 'gasoline' | 'diesel' | 'electric' | 'hybrid';
  year: number;
  kilometers: number | null;
  vin_number: string | null;
  conductor_password: string | null;
  last_service_date: string | null;
  next_service_date: string | null;
  color: string | null;
  transmission: string | null;
  engine_size: string | null;
  insurance_expiry: string | null;
  registration_expiry: string | null;
  status: 'active' | 'inactive' | 'maintenance';
  conductor_name: string;
}

interface ClientServicePrice {
  id: number;
  client_id: number;
  vehicle_id: number;
  service_id: number;
  price: number;
  service: {
    name: string;
  };
}

interface Service {
  id: number;
  name: string;
}

const VehicleInfoItem: React.FC<{ icon: React.ReactNode; label: string; value: string | number | null }> = ({ icon, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full mr-3">
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value || '-'}</p>
    </div>
  </div>
);

export default function VehicleDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [clientServicePrices, setClientServicePrices] = useState<ClientServicePrice[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [priceDialog, setPriceDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<Partial<ClientServicePrice> | null>(null);
  const [copyPricesDialog, setCopyPricesDialog] = useState(false);
  const [sourcePlateNumber, setSourcePlateNumber] = useState('');
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const [copyMode, setCopyMode] = useState('overwrite');

  useEffect(() => {
    fetchVehicleData();
    fetchServices();
  }, [id]);

  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const [vehicleRes, pricesRes] = await Promise.all([
        api.get(`/vehicles/${id}`),
        api.get(`/vehicles/${id}/client-service-prices`)
      ]);

      setVehicle(vehicleRes.data);
      setClientServicePrices(pricesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      showToast('error', t('error_fetching_vehicle_data'));
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      showToast('error', t('error_fetching_services'));
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' });
  };

  const openNewPrice = () => {
    setEditingPrice({});
    setPriceDialog(true);
  };

  const hidePriceDialog = () => {
    setPriceDialog(false);
    setEditingPrice(null);
  };

  const savePrice = async () => {
    if (!editingPrice) return;

    try {
      if (editingPrice.id) {
        await api.put(`/client-service-prices/${editingPrice.id}`, editingPrice);
        showToast('success', t('price_updated'));
      } else {
        await api.post(`/vehicles/${id}/client-service-prices`, { ...editingPrice, vehicle_id: id });
        showToast('success', t('price_created'));
      }
      hidePriceDialog();
      fetchVehicleData();
    } catch (error) {
      console.error('Error saving price:', error);
      showToast('error', t('error_saving_price'));
    }
  };

  const editPrice = (price: ClientServicePrice) => {
    setEditingPrice({ ...price });
    setPriceDialog(true);
  };

  const confirmDeletePrice = (price: ClientServicePrice) => {
    if (window.confirm(t('delete_price_confirmation', { service: price.service.name }))) {
      deletePrice(price.id);
    }
  };

  const deletePrice = async (priceId: number) => {
    try {
      await api.delete(`/client-service-prices/${priceId}`);
      showToast('success', t('price_deleted'));
      fetchVehicleData();
    } catch (error) {
      console.error('Error deleting price:', error);
      showToast('error', t('error_deleting_price'));
    }
  };

  const openCopyPricesDialog = () => {
    setCopyPricesDialog(true);
  };

  const hideCopyPricesDialog = () => {
    setCopyPricesDialog(false);
    setSourcePlateNumber('');
  };

  const copyPrices = async () => {
    try {
      const response = await api.post(`/vehicles/${id}/copy-prices`, { 
        source_plate_number: sourcePlateNumber,
        copy_mode: copyMode
      });
      setClientServicePrices(response.data);
      showToast('success', t('prices_copied'));
      hideCopyPricesDialog();
    } catch (error) {
      console.error('Error copying prices:', error);
      showToast('error', t('error_copying_prices'));
    }
  };

  const showToast = (type: string, message: string) => {
    setToast({ visible: true, type, message });
    setTimeout(() => setToast({ visible: false, type: '', message: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse bg-gray-200 h-40 mb-4 rounded-lg"></div>
        <div className="animate-pulse bg-gray-200 h-96 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {vehicle && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start mb-6">
            <img 
              src={`${process.env.REACT_APP_PIC_STORAGE}/${vehicle.logo_url}`} 
              alt={vehicle.brand_name} 
              className="w-24 h-24 md:w-32 md:h-32 object-contain mb-4 md:mb-0 md:mr-6"
            />
            <div>
              <h1 className="text-3xl font-bold mb-2">{vehicle.plate_number}</h1>
              <p className="text-xl text-gray-600 mb-4">{`${vehicle.brand_name} ${vehicle.model} (${vehicle.year})`}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <VehicleInfoItem icon={<Car className="text-blue-500" />} label={t('kilometers')} value={vehicle.kilometers ? `${vehicle.kilometers} km` : null} />
            <VehicleInfoItem icon={<Hash className="text-blue-500" />} label={t('vin_number')} value={vehicle.vin_number} />
            <VehicleInfoItem icon={<CircleUserRound className="text-blue-500" />} label={t('conductor_name')} value={vehicle.conductor_name ? vehicle.conductor_name : "N/A"} />
            <VehicleInfoItem icon={<Calendar className="text-blue-500" />} label={t('last_service_date')} value={vehicle.last_service_date ? new Date(vehicle.last_service_date).toLocaleDateString() : null} />
            <VehicleInfoItem icon={<Fuel className="text-blue-500" />} label={t('fuel_type')} value={t(vehicle.fuel_type)} />
            <VehicleInfoItem icon={<Antenna className="text-blue-500" />} label={t('transmission')} value={t(vehicle.transmission)} />
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">{t('client_service_prices')}</h2>
          <div className="space-x-2">
            <button
              onClick={openNewPrice}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
            >
              <Plus size={14} className="inline-block mr-2" />
              {t('add_price')}
            </button>
            <button
              onClick={openCopyPricesDialog}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition duration-200"
            >
              <Copy size={14} className="inline-block mr-2" />
              {t('copy_prices')}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('service')}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('price')}</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clientServicePrices.map((price) => (
                <tr key={price.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{price.service.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(price.price)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => editPrice(price)}
                      className="text-indigo-600 hover:text-indigo-900 mr-2"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => confirmDeletePrice(price)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {priceDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingPrice && editingPrice.id ? t('edit_price') : t('new_price')}</h2>
              <button onClick={hidePriceDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); savePrice(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="service_id" className="block text-sm font-medium text-gray-700">{t('service')}</label>
                  <select
                    id="service_id"
                    value={editingPrice?.service_id || ''}
                    onChange={(e) => setEditingPrice({ ...editingPrice, service_id: parseInt(e.target.value) })}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">{t('select_service')}</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">{t('price')}</label>
                  <input
                    type="number"
                    id="price"
                    value={editingPrice?.price || ''}
                    onChange={(e) => setEditingPrice({ ...editingPrice, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hidePriceDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {copyPricesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{t('copy_prices')}</h2>
              <button onClick={hideCopyPricesDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); copyPrices(); }} className="p-6">
              <div>
                <label htmlFor="sourcePlateNumber" className="block text-sm font-medium text-gray-700 mb-2">{t('source_plate_number')}</label>
                <input
                  type="text"
                  id="sourcePlateNumber"
                  value={sourcePlateNumber}
                  onChange={(e) => setSourcePlateNumber(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('copy_mode')}</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="overwrite"
                      checked={copyMode === 'overwrite'}
                      onChange={() => setCopyMode('overwrite')}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('overwrite_existing_prices')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="remove_add"
                      checked={copyMode === 'remove_add'}
                      onChange={() => setCopyMode('remove_add')}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('remove_old_add_new_prices')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="add_only"
                      checked={copyMode === 'add_only'}
                      onChange={() => setCopyMode('add_only')}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('add_new_prices_only')}</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideCopyPricesDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('copy')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

